<script lang="ts" setup>
import type { ApiOfficeserviceOffices } from '~~/server/api/officeservice/offices'
import type { ApiOfficeserviceServices } from '~~/server/api/officeservice/services'

export interface WidgetOfficesMapProps {
    data: ApiOfficeserviceOffices | ApiOfficeserviceServices
    mapHeight?: string
    title: string
    titleModal: string
    pending: boolean
    namedRouteKey: string
    position: string
    type: string
}

const { $t } = useNuxtApp()

const placeholder = $t('Hľadajte mesto')
const detail = $t('Detail')

const config = useRuntimeConfig()
const props = defineProps<WidgetOfficesMapProps>()
const route = useRoute()
const zoom = ref<number>(config.public.gmap.zoomMin)
const autocomplete = ref<any>()
const defaultMapCenter = {
    lng: 19.1573494,
    lat: 48.7384028,
} // for Banska Bystrica
const mapCenter = computed<MapCoords>(() => {
    return {
        lng: Number(defaultMapCenter.lng),
        lat: Number(defaultMapCenter.lat),
    }
})
const mapMarkers = computed<MapMarker[]>((): MapMarker[] =>
    props.data.data.map((office): MapMarker => {
        return {
            lat: Number(office.lat),
            lng: Number(office.lng),
            data: office,
            title: `${office.address} ${office.city}`,
        }
    })
)
const officesMapStore = useOfficesMapStore()
const router = useRouter()
const showList = computed<boolean>(
    (): boolean =>
        !!route.query?.query?.length ||
        Boolean(route.query?.lat && route.query?.lng)
)

const resetMap = (): void => {
    // reset zoom
    zoom.value = config.public.gmap.zoomMin
    // disable active marker
    officesMapStore.value.activeMarker = undefined
}

const handleSubmitSearchForm = (): void => {
    resetMap()
    // select first
    autocomplete.value?.$refs?.input?.dispatchEvent(
        new KeyboardEvent('keydown', { keyCode: 13 })
    )
}

const handlePlaceChange = (value: google.maps.GeocoderResult): void => {
    // reset map
    resetMap()
    // update url
    router.push({
        query: {
            ...route.query,
            lat: value?.geometry?.location?.lat(),
            lng: value?.geometry?.location?.lng(),
            query: '',
        },
        hash: route.hash,
    })
}
</script>
<template>
  <DelayHydration>
    <div class="row justify-content-center mb-2 mt-8 mt-md-0 mb-md-8">
      <Tracker :data="{ position, title: props.title }">
        <div class="col-12 col-md-8 col-lg-7 mb-4 mb-md-0">
          <SearchForm
            control-name="query"
            :submit="false"
            :placeholder="placeholder"
            :action="useNamedRoutes({ name: 'offices' })"
            :has-slots="true"
            :navigate-on-search="false"
            @submit.prevent="handleSubmitSearchForm"
          >
            <GMapAutocompleteWrapper class="flex-grow-1">
              <GMapAutocomplete
                ref="autocomplete"
                :select-first-on-enter="true"
                :placeholder="placeholder"
                :aria-label="placeholder"
                :component-restrictions="{ country: 'sk' }"
                @place_changed="handlePlaceChange"
              />
            </GMapAutocompleteWrapper>
          </SearchForm>
        </div>
      </Tracker>
    </div>
    <div
      class="bg-white rounded box-shadow-1 overflow-hidden position-relative"
    >
      <Tracker :data="{ position: props.position, title: props.title }">
        <div
          :class="{
            'offices-map-wrapper': showList,
          }"
        >
          <section
            v-if="showList"
            class="offices-map-wrapper-content px-4 py-4 px-md-8 py-md-6 d-flex flex-column"
            :style="{
              '--offices-map-wrapper-content-height': props.mapHeight,
              '--scrollbar-size': '1rem',
            }"
          >
            <div>
              <div>
                <div class="d-flex align-items-center mb-4">
                  <h3
                    ref="resultsHeadingRef"
                    class="delta mb-0 d-flex"
                    tabindex="0"
                    :class="{ 'me-2': mapMarkers.length > 0 }"
                  >
                    {{ mapMarkers.length > 0 ? `${props.title} (${mapMarkers.length})` : props.title }}
                  </h3>
                </div>
                <slot />
              </div>
              <OfficesMapList
                v-if="mapMarkers.length > 0"
                class="d-none d-md-block flex-grow-1 h-100 mt-md-6"
                :offices="mapMarkers"
              />
              <SearchResultsEmpty
                v-else
                tabindex="0"
                class="mt-md-10"
              />
            </div>
          </section>
          <div class="bg-gray-1">
            <Tracker :data="{ title: props.titleModal }">
              <Map
                :data="{
                  mapOptions: {
                    height: String(props.mapHeight),
                    center: mapCenter,
                    zoom,
                    clusters: !showList,
                  },
                  markers: mapMarkers,
                }"
              >
                <template #modal="marker: any">
                  <MapModalOffice
                    :office="marker?.data"
                    :title="props.titleModal"
                    :type="props.type"
                  >
                    <template #button>
                      <VButton
                        size="small"
                        color="primary"
                        :label="detail"
                        :aria-label="`Detail pobočky: ${marker?.data.address}, ${marker?.data.city}`"
                        :to="{
                          name: props.namedRouteKey,
                          params: {
                            id: marker?.data.slug,
                          },
                        }"
                      >
                        {{ detail }}
                      </VButton>
                    </template>
                  </MapModalOffice>
                </template>
              </Map>
            </Tracker>
          </div>
        </div>
      </Tracker>

      <LoadingBar
        v-if="pending"
        color="white"
      />
    </div>
  </DelayHydration>
</template>
<style lang="scss">
@import './OfficesMap.scss';
</style>
